import { jsx, jsxs } from "@ecopages/jsx/jsx-runtime";
import { withDefaultAriaLabel } from "@/aria";
import { coalesceDefined } from "@/lib/coalesce-defined";
import { cx } from "@/lib/cx";
import { omitProps } from "@/lib/omit-props";
import "./sidebar.script";
import "./sidebar-trigger.script";
export function RuiSidebarProvider({
  layout = "default",
  siteHeader,
  sidebar,
  children,
  class: className,
  ...props
}) {
  if (layout === "docs") {
    return /* @__PURE__ */jsxs("div", {
      ...props,
      class: cx("rui-sidebar-provider", className),
      "data-layout": layout,
      children: [siteHeader ? /* @__PURE__ */jsx("div", {
        class: "rui-sidebar-provider__site-header",
        children: siteHeader
      }) : null, /* @__PURE__ */jsxs("div", {
        class: "rui-sidebar-provider__body",
        children: [sidebar, children]
      })]
    });
  }
  return /* @__PURE__ */jsxs("div", {
    ...props,
    class: cx("rui-sidebar-provider", className),
    "data-layout": layout,
    children: [sidebar, children]
  });
}
export function RuiSidebarHeader({
  children,
  class: className,
  ...props
}) {
  return /* @__PURE__ */jsx("div", {
    ...props,
    class: cx("rui-sidebar__header", className),
    children
  });
}
export function RuiSidebarContent({
  children,
  class: className,
  ...props
}) {
  return /* @__PURE__ */jsx("div", {
    ...props,
    class: cx("rui-sidebar__content", className),
    children
  });
}
export function RuiSidebarFooter({
  children,
  class: className,
  ...props
}) {
  return /* @__PURE__ */jsx("div", {
    ...props,
    class: cx("rui-sidebar__footer", className),
    children
  });
}
export function RuiSidebarSeparator({
  class: className,
  ...props
}) {
  return /* @__PURE__ */jsx("hr", {
    ...props,
    role: "separator",
    "aria-orientation": "horizontal",
    class: cx("rui-sidebar__separator", className)
  });
}
export function RuiSidebarGroup({
  children,
  class: className,
  ...props
}) {
  return /* @__PURE__ */jsx("section", {
    ...props,
    class: cx("rui-sidebar__group", className),
    children
  });
}
export function RuiSidebarGroupLabel({
  children,
  class: className,
  ...props
}) {
  return /* @__PURE__ */jsx("h2", {
    ...props,
    class: cx("rui-sidebar__group-label", className),
    children
  });
}
export function RuiSidebarGroupAction({
  children,
  class: className,
  ...props
}) {
  return /* @__PURE__ */jsx("button", {
    ...props,
    type: "button",
    class: cx("rui-sidebar__group-action", className),
    children
  });
}
export function RuiSidebarGroupHeader({
  label,
  action,
  class: className,
  ...props
}) {
  return /* @__PURE__ */jsxs("div", {
    ...props,
    class: cx("rui-sidebar__group-header", className),
    children: [/* @__PURE__ */jsx(RuiSidebarGroupLabel, {
      children: label
    }), action]
  });
}
export function RuiSidebarMenu({
  children,
  class: className,
  ...props
}) {
  return /* @__PURE__ */jsx("ul", {
    ...props,
    role: "list",
    class: cx("rui-sidebar__menu", className),
    children
  });
}
export function RuiSidebarMenuItem({
  children,
  class: className,
  ...props
}) {
  return /* @__PURE__ */jsx("li", {
    ...props,
    class: cx("rui-sidebar__menu-item", className),
    children
  });
}
export function RuiSidebarMenuButton(props) {
  if (props.href !== void 0) {
    const {
      children: children2,
      class: className2,
      isActive: isActive2,
      href,
      tooltip: tooltip2,
      aria: aria2,
      title: title2,
      "aria-current": ariaCurrent2,
      ...host2
    } = props;
    return /* @__PURE__ */jsx("a", {
      ...omitProps(host2, "as"),
      aria: withDefaultAriaLabel(aria2, typeof children2 === "string" ? void 0 : tooltip2),
      href,
      class: cx("rui-sidebar__menu-button", isActive2 && "rui-sidebar__menu-button--active", className2),
      "aria-current": isActive2 ? "page" : ariaCurrent2,
      title: coalesceDefined(title2, tooltip2),
      children: children2
    });
  }
  const {
    children,
    class: className,
    isActive,
    type = "button",
    tooltip,
    disabled,
    onClick,
    aria,
    title,
    "aria-current": ariaCurrent,
    "on:click": onHostClick,
    ...host
  } = props;
  return /* @__PURE__ */jsx("button", {
    ...omitProps(host, "as", "href"),
    aria: withDefaultAriaLabel(aria, typeof children === "string" ? void 0 : tooltip),
    type,
    class: cx("rui-sidebar__menu-button", isActive && "rui-sidebar__menu-button--active", className),
    "aria-current": isActive ? "page" : ariaCurrent,
    disabled,
    title: coalesceDefined(title, tooltip),
    "on:click": coalesceDefined(onHostClick, onClick),
    children
  });
}
export function RuiSidebarMenuAction(props) {
  const {
    class: className,
    ...rest
  } = props;
  return /* @__PURE__ */jsx(RuiSidebarMenuButton, {
    class: cx("rui-sidebar__menu-action", className),
    ...rest
  });
}
export function RuiSidebarInset({
  children,
  class: className,
  ...props
}) {
  return /* @__PURE__ */jsx("main", {
    ...props,
    class: cx("rui-sidebar__inset", className),
    children
  });
}
export function RuiSidebar({
  children,
  open,
  label,
  ...props
}) {
  return /* @__PURE__ */jsx("rui-sidebar", {
    ...props,
    "prop:open": open,
    label,
    children: /* @__PURE__ */jsxs("div", {
      class: "rui-sidebar",
      "data-ref": "root",
      children: [/* @__PURE__ */jsx("button", {
        "data-ref": "scrim",
        type: "button",
        class: "rui-sidebar__scrim",
        tabindex: -1,
        "aria-label": "Close sidebar",
        hidden: true
      }), /* @__PURE__ */jsx("div", {
        "data-ref": "pane",
        class: "rui-sidebar__pane",
        "aria-label": label,
        children
      }), /* @__PURE__ */jsx("div", {
        "data-ref": "handle",
        class: "rui-sidebar__handle",
        role: "separator",
        tabindex: 0,
        hidden: true,
        children: /* @__PURE__ */jsx("span", {
          class: "rui-sidebar__handle-grip",
          "aria-hidden": "true"
        })
      })]
    })
  });
}
function RuiSidebarTriggerIcon() {
  return /* @__PURE__ */jsxs("span", {
    class: "rui-sidebar__trigger-icon",
    "aria-hidden": "true",
    children: [/* @__PURE__ */jsxs("svg", {
      class: "rui-sidebar__trigger-glyph rui-sidebar__trigger-glyph--collapse",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      "stroke-width": "2",
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
      children: [/* @__PURE__ */jsx("path", {
        d: "M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"
      }), /* @__PURE__ */jsx("path", {
        d: "M9 3v18"
      }), /* @__PURE__ */jsx("path", {
        d: "m14 15 3-3-3-3"
      })]
    }), /* @__PURE__ */jsxs("svg", {
      class: "rui-sidebar__trigger-glyph rui-sidebar__trigger-glyph--expand",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      "stroke-width": "2",
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
      children: [/* @__PURE__ */jsx("path", {
        d: "M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"
      }), /* @__PURE__ */jsx("path", {
        d: "M9 3v18"
      }), /* @__PURE__ */jsx("path", {
        d: "m14 9-3 3 3 3"
      })]
    }), /* @__PURE__ */jsxs("svg", {
      class: "rui-sidebar__trigger-glyph rui-sidebar__trigger-glyph--close",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      "stroke-width": "2",
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
      children: [/* @__PURE__ */jsx("path", {
        d: "M18 6 6 18"
      }), /* @__PURE__ */jsx("path", {
        d: "m6 6 12 12"
      })]
    }), /* @__PURE__ */jsxs("svg", {
      class: "rui-sidebar__trigger-glyph rui-sidebar__trigger-glyph--menu",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      "stroke-width": "2",
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
      children: [/* @__PURE__ */jsx("path", {
        d: "M4 6h16"
      }), /* @__PURE__ */jsx("path", {
        d: "M4 12h16"
      }), /* @__PURE__ */jsx("path", {
        d: "M4 18h16"
      })]
    })]
  });
}
export function RuiSidebarTrigger({
  children,
  class: className,
  triggerLabel,
  placement,
  variant = "ghost",
  size = "md",
  ...props
}) {
  const buttonLabel = triggerLabel ?? "Toggle sidebar";
  return /* @__PURE__ */jsx("rui-sidebar-trigger", {
    ...props,
    class: cx(className, placement && `rui-sidebar-trigger-placement--${placement}`),
    "prop:buttonLabel": buttonLabel,
    data: {
      buttonLabel
    },
    placement,
    variant,
    size,
    children: /* @__PURE__ */jsx("button", {
      "data-ref": "button",
      type: "button",
      class: cx("rui-button", `rui-button--${variant}`, `rui-button--${size}`, "rui-sidebar__trigger"),
      children: children ?? /* @__PURE__ */jsx(RuiSidebarTriggerIcon, {})
    })
  });
}